import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ScrapedJob {
  id: string;
  titulo: string;
  empresa: string;
  descripcion: string;
  ubicacion: string;
  modalidad: string;
  salarioMin?: number;
  salarioMax?: number;
  urlOriginal: string;
  urlVacante?: string;
  fuente: string;
  idCategoria: number;
  categoriaNombre: string;
  tecnologias: string[];
  status: string;
  scrapedAt: string;
  publishedAt?: string;
  fechaPublicacion?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

@Injectable({ providedIn: 'root' })
export class ScrapedJobService {

  private pub   = `${environment.baseApi}/publico/vacantes-externas`;
  private admin = `${environment.baseApi}/admin`;

  constructor(private http: HttpClient) {}

  // ── Público ────────────────────────────────────────────────────────────────
  listar(page = 0, size = 20): Observable<any> {
    return this.http.get(`${this.pub}?page=${page}&size=${size}`);
  }

  listarPorCategoria(idCategoria: number, page = 0, size = 20): Observable<any> {
    return this.http.get(`${this.pub}/categoria/${idCategoria}?page=${page}&size=${size}`);
  }

  getById(id: string): Observable<any> {
    return this.http.get(`${this.pub}/${id}`);
  }

  guruMatchGlobal(candidatoId: number): Observable<any> {
    return this.http.get(`${environment.baseApi}/candidato/gurumatch/${candidatoId}/global`);
  }

  guruMatchHistory(candidatoId: number): Observable<any> {
    return this.http.get(`${environment.baseApi}/candidato/gurumatch/${candidatoId}/history`);
  }

  freeOnboarding(): Observable<any> {
    return this.http.post(`${environment.baseApi}/candidato/gurumatch/free-onboarding`, {});
  }

  // ── Admin (JWT requerido — el interceptor lo añade automáticamente) ────────
  listarPendientes(page = 0, size = 50): Observable<any> {
    return this.http.get(`${this.admin}/vacantes-externas/pendientes?page=${page}&size=${size}`);
  }

  ejecutarScraper(): Observable<any> {
    return this.http.post(`${this.admin}/scraper/ejecutar`, {});
  }

  detenerScraper(): Observable<any> {
    return this.http.post(`${this.admin}/scraper/detener`, {});
  }

  stats(): Observable<any> {
    return this.http.get(`${this.admin}/vacantes-externas/stats`);
  }

  rechazar(id: string): Observable<any> {
    return this.http.patch(`${this.admin}/vacantes-externas/${id}/rechazar`, {});
  }

  getConfig(): Observable<any> {
    return this.http.get(`${this.admin}/scraper/config`);
  }

  getTokens(): Observable<any> {
    return this.http.get(`${this.admin}/scraper/config/tokens`);
  }

  updateConfig(config: any): Observable<any> {
    return this.http.post(`${this.admin}/scraper/config`, config);
  }

  getHistorial(): Observable<any> {
    return this.http.get(`${this.admin}/scraper/historial`);
  }

  countOrphans(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.admin}/scraper/orphans/count`);
  }

  deleteOrphans(): Observable<any> {
    return this.http.delete(`${this.admin}/scraper/orphans`);
  }

  fixStatus(): Observable<any> {
    return this.http.post(`${this.admin}/scraper/fix-status`, {});
  }

  getRunVacantes(runId: number): Observable<any> {
    return this.http.get(`${this.admin}/scraper/historial/${runId}/vacantes`);
  }

  getRunResultados(runId: number): Observable<any> {
    return this.http.get(`${this.admin}/scraper/historial/${runId}/resultados`);
  }

  eliminarRun(runId: number): Observable<any> {
    return this.http.delete(`${this.admin}/scraper/historial/${runId}`);
  }

  rechazarVacanteIndividual(vacanteId: number): Observable<any> {
    return this.http.delete(`${this.admin}/scraper/vacantes/${vacanteId}`);
  }

  getScraperStatus(): Observable<{ data: { running: boolean; fuente: string; count: number; total: number } }> {
    return this.http.get<any>(`${this.admin}/scraper/status`);
  }

  getApifyBalance(actorId?: string): Observable<any> {
    const suffix = actorId ? `?actorId=${encodeURIComponent(actorId)}` : '';
    return this.http.get(`${this.admin}/scraper/apify-balance${suffix}`);
  }
  proxyTest(proxy: string): Observable<any> {
    return this.http.post(`${this.admin}/scraper/proxy-test`, { proxy });
  }
}
